<template>
  <div v-if="show" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click.self="handleClose">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" @click.stop>
      <div class="mt-3">
        <!-- Modal Header -->
        <div class="flex items-center justify-between pb-4 border-b border-gray-200">
          <div class="flex items-center">
            <svg class="h-6 w-6 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 class="text-lg font-medium text-gray-900">
              {{ $t('Fehlende Informationen') }}
            </h3>
          </div>
          <button
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="mt-4">
          <div class="bg-red-50 border-l-4 border-red-400 p-4">
            <div class="flex">
              <div class="ml-3">
                <p class="text-sm text-red-700">
                  {{ $t('Bitte gib die folgenden erforderlichen Informationen an, bevor du deine Bestellung aufgibst:') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Missing Information List -->
          <div class="mt-4">
            <ul class="space-y-2">
              <li v-if="missingInfo.shippingAddress" class="flex items-start">
                <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <div>
                  <span class="text-sm font-medium text-gray-900">{{ $t('Lieferadresse') }}</span>
                  <p class="text-sm text-gray-600 mt-1">{{ $t('Bitte wähle eine Lieferadresse aus') }}</p>
                </div>
              </li>

              <li v-if="missingInfo.billingAddress" class="flex items-start">
                <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <div>
                  <span class="text-sm font-medium text-gray-900">{{ $t('Rechnungsadresse') }}</span>
                  <p class="text-sm text-gray-600 mt-1">{{ $t('Bitte wähle eine Rechnungsadresse aus') }}</p>
                </div>
              </li>

              <li v-if="missingInfo.invoiceEmail" class="flex items-start">
                <svg class="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <div>
                  <span class="text-sm font-medium text-gray-900">{{ $t('Rechnungs-E-Mail') }}</span>
                  <p class="text-sm text-gray-600 mt-1">{{ $t('Bitte gib eine gültige Rechnungs-E-Mail-Adresse an') }}</p>
                </div>
              </li>
            </ul>
          </div>

          <!-- Help Information -->
          <div class="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  {{ $t('So vervollständigst du deine Bestellung') }}
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>{{ $t('Du kannst deine Adressen und Rechnungs-E-Mail in den Kontoeinstellungen hinzufügen oder bearbeiten.') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="mt-6 flex justify-between gap-3">
          <a
            href="/partner-account"
            class="inline-flex items-center px-4 py-2 border border-sonnenglas-600 text-sm font-medium rounded-md text-sonnenglas-700 bg-white hover:bg-sonnenglas-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
          >
            {{ $t('Zu den Kontoeinstellungen') }}
          </a>
          <button
            @click="handleClose"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {{ $t('Schließen') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'

interface MissingInfo {
  shippingAddress: boolean
  billingAddress: boolean
  invoiceEmail: boolean
}

interface Props {
  show: boolean
  missingInfo: MissingInfo
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Get the global $t function from Vue instance
const instance = getCurrentInstance()
const $t = instance?.appContext.config.globalProperties.$t || ((key: string) => key)

const handleClose = () => {
  emit('close')
}
</script>
